
import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

export default function TeacherRoster() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [attendanceFilter, setAttendanceFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const teacherEmail = user?.email;

  const { data: myRosterEntries = [], isLoading } = useQuery({
    queryKey: ['myRosterEntries', teacherEmail],
    queryFn: async () => {
      if (!teacherEmail) return [];
      // Only show active entries (after closeout, completed/excused/asd entries are filtered out)
      const allEntries = await base44.entities.RosterEntry.filter({ teacher_email: teacherEmail }, '-created_date');
      return allEntries.filter(e => e.status === 'active');
    },
    enabled: !!teacherEmail,
  });

  const filteredEntries = myRosterEntries.filter(entry => {
    const matchesSearch = 
      entry.student_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.student_last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.course_short_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.other_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || entry.status === statusFilter;
    const matchesAttendance = attendanceFilter === "all" || entry.attendance_status === attendanceFilter;
    
    return matchesSearch && matchesStatus && matchesAttendance;
  });

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle null/undefined values
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Convert to lowercase for string comparison
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="w-4 h-4 ml-1 text-blue-600" /> : 
      <ArrowDown className="w-4 h-4 ml-1 text-blue-600" />;
  };

  const getAttendanceBadge = (status, rosterStatus) => {
    // Check if the entry is excused (status field, not attendance_status)
    if (rosterStatus === 'excused') {
      return { color: 'bg-orange-500 text-white font-bold', label: 'E' };
    }
    
    const badges = {
      present: { color: 'bg-green-500 text-white font-bold', label: 'P' },
      absent: { color: 'bg-yellow-500 text-white font-bold', label: 'A' },
      refusal: { color: 'bg-red-500 text-white font-bold', label: 'R' },
      pending: { color: 'bg-slate-300 text-slate-700 font-bold', label: 'Pending' }
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-full mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">My Roster</h1>
          <p className="text-slate-600 mt-1">My Assigned Students ({sortedEntries.length} Entries)</p>
        </div>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, Other ID, or course..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="excused">Excused</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={attendanceFilter} onValueChange={setAttendanceFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Attendance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Attendance</SelectItem>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="refusal">Refusal</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">Attendance</TableHead>
                    <TableHead className="min-w-[100px]">Campus</TableHead>
                    <TableHead 
                      className="min-w-[120px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('student_last_name')}
                    >
                      <div className="flex items-center">
                        Student Last Name
                        {getSortIcon('student_last_name')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="min-w-[120px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('student_first_name')}
                    >
                      <div className="flex items-center">
                        Student First Name
                        {getSortIcon('student_first_name')}
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[100px]">Other ID</TableHead>
                    <TableHead className="min-w-[100px]">Student ID</TableHead>
                    <TableHead className="min-w-[120px]">Method of Instruction</TableHead>
                    <TableHead 
                      className="min-w-[80px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('grade_level')}
                    >
                      <div className="flex items-center">
                        Grade
                        {getSortIcon('grade_level')}
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[120px]">Course</TableHead>
                    <TableHead className="min-w-[80px]">Section</TableHead>
                    <TableHead className="min-w-[150px]">Course Short Description</TableHead>
                    <TableHead className="min-w-[120px]">Teacher</TableHead>
                    <TableHead className="min-w-[150px]">Assignment</TableHead>
                    <TableHead className="min-w-[100px]">Assignment Grade</TableHead>
                    <TableHead className="min-w-[100px]">Athletic Grade</TableHead>
                    <TableHead className="min-w-[150px]">Comments</TableHead>
                    <TableHead className="min-w-[100px]">Date</TableHead>
                    <TableHead className="min-w-[80px]">Time</TableHead>
                    <TableHead className="min-w-[120px]">F1G1 Parent Phone</TableHead>
                    <TableHead className="min-w-[120px]">F1G2 Parent Phone</TableHead>
                    <TableHead className="min-w-[150px]">Student Email</TableHead>
                    <TableHead className="min-w-[150px]">F1G1 Parent Email</TableHead>
                    <TableHead className="min-w-[120px]">F2G1 Parent Phone</TableHead>
                    <TableHead className="min-w-[150px]">F2G1 Parent Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={24} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={24} className="text-center py-8 text-slate-500">
                        No entries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedEntries.map((entry) => {
                      const badge = getAttendanceBadge(entry.attendance_status, entry.status);
                      return (
                        <TableRow key={entry.id} className="hover:bg-slate-50">
                          <TableCell>
                            <Badge className={badge.color}>
                              {badge.label}
                            </Badge>
                          </TableCell>
                          <TableCell>{entry.campus || '-'}</TableCell>
                          <TableCell className="font-medium">{entry.student_last_name}</TableCell>
                          <TableCell className="font-medium">{entry.student_first_name}</TableCell>
                          <TableCell className="font-mono text-sm font-bold">{entry.other_id}</TableCell>
                          <TableCell className="font-mono text-sm">{entry.student_id || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.method_of_instruction || '-'}</TableCell>
                          <TableCell>{entry.grade_level || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.course || '-'}</TableCell>
                          <TableCell>{entry.section || '-'}</TableCell>
                          <TableCell>{entry.course_short_description || '-'}</TableCell>
                          <TableCell>{entry.teacher}</TableCell>
                          <TableCell className="text-sm">{entry.assignment || '-'}</TableCell>
                          <TableCell>
                            <span className="font-semibold text-red-600">{entry.assignment_grade || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="font-semibold text-red-600">{entry.athletic_grade || '-'}</span>
                          </TableCell>
                          <TableCell className="text-sm">{entry.comments || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.date || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.time || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{entry.f1g1_parent_phone || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{entry.f1g2_parent_phone || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.student_email || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.f1g1_parent_email || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{entry.f2g1_parent_phone || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.f2g1_parent_email || '-'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
